import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Table } from '@components/atoms/Table';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { Card } from '@components/atoms/Card';
import { StoreStateBadge } from '@components/molecules/badges/StoreStateBadge';
import { SITE_URLS } from '@constants';
import { usePermissionChecker, UserPermissions } from '@hooks/useHasPermission';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useData } from '@hooks/useData';
import { useNotifications } from '@hooks/useNotifications';
import { useUpdateStateStore } from '@services/useUpdateStateStore';
import { useGetAllMyAccountStores } from '@services/useGetAllMyAccountStores';
import styles from './MyCompanies.module.css';
import { StorePaymentIntegrationStateBadge } from '@components/molecules/badges/StorePaymentIntegrationStateBadge';

export const MyCompanies: FC = () => {
    const { navigate } = useCustomNavigate();
    const { can } = usePermissionChecker();
    const { t } = useTranslation();

    const translations = {
        title: t('my-companies-component.title'),
        addStore: t('my-companies-component.add-store'),
    };

    // if user does not have permission accessMerchantAreas, he can't access this page
    useEffect(() => {
        if (can(UserPermissions.accessMerchantAreas) === false) {
            navigate(`/${SITE_URLS.myProfile}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <MyAccountContainer title={translations.title}>
            <div className="pt-4 pb-2">
                {/* add store */}
                <div className="text-md-end">
                    <Anchor href={SITE_URLS.myCompaniesCreate} mode={AnchorMode.primary}>
                        {translations.addStore}
                    </Anchor>
                </div>
            </div>
            <div className="pt-4">
                <CompaniesTable />
            </div>
        </MyAccountContainer>
    );
};

const CompaniesTable: FC = () => {
    const { showDefaultNotification } = useNotifications();
    const {
        request: requestUpdateState,
        loading: loadingUpdateState,
        error: errorUpdateState,
        success: successUpdateState,
        lastResponseTime,
    } = useUpdateStateStore();
    const { request: requestAllMyAccountStores } = useGetAllMyAccountStores();
    const { allMyAccountStores, allSettings } = useData();
    const { t } = useTranslation();

    const translations = {
        logo: t('my-companies-component.table.logo'),
        name: t('my-companies-component.table.name'),
        state: t('my-companies-component.table.state'),
        actions: t('my-companies-component.table.actions'),
        activate: t('my-companies-component.activate'),
        deactivate: t('my-companies-component.deactivate'),
        paymentIntegration: t('my-companies-component.payment-integration'),
    };

    // call services
    useEffect(() => {
        requestAllMyAccountStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // dispatch notification, according to feedback of service
    useEffect(() => {
        if (lastResponseTime) {
            if (errorUpdateState) {
                showDefaultNotification({ type: 'error', message: errorUpdateState });
            } else {
                if (successUpdateState) {
                    showDefaultNotification({ type: 'success', message: successUpdateState });
                }
                requestAllMyAccountStores();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    const handleOnUpdateState: (uuid: string) => void = (uuid) => {
        requestUpdateState({ uuid });
    };

    return (
        <div data-testid="management-table">
            <Card classes="mb-0">
                <Table
                    borderless={true}
                    headerCols={[
                        { classes: `text-left`, text: translations.logo },
                        { classes: `text-left`, text: translations.name },
                        { classes: 'text-center', text: translations.state },
                        { classes: 'text-center', text: translations.paymentIntegration },
                        { classes: 'text-end', fit: true, text: translations.actions },
                    ]}
                    contentRows={(allMyAccountStores?.data || []).map((item) => {
                        return [
                            {
                                classes: 'text-left',
                                content: (
                                    <>{item.logo?.src && <img src={item.logo.src} className={styles.logo} alt={item.name} />}</>
                                ),
                            },
                            {
                                classes: 'text-left',
                                content: <Typography fontSize="14">{item.name}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: <StoreStateBadge state={item.status || ''} />,
                            },
                            {
                                classes: 'text-center',
                                content: (
                                    <StorePaymentIntegrationStateBadge state={item.has_split_account ? 'active' : 'inactive'} />
                                ),
                            },
                            {
                                classes: 'text-end',
                                content: (
                                    <Table.Options
                                        options={
                                            [
                                                item.can_have_services &&
                                                    allSettings?.data?.module_services?.schedule_by_employee && (
                                                        <Anchor
                                                            key="option-0"
                                                            href={`${SITE_URLS.myWorkers.replace(':store', `${item.uuid}`)}`}
                                                            mode={AnchorMode.none}>
                                                            <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                                <i className="fa-light fa-users"></i>
                                                            </Typography>
                                                        </Anchor>
                                                    ),
                                                item.can_have_services &&
                                                    allSettings?.data?.module_services?.schedule_management && (
                                                        <Anchor
                                                            key="option-1"
                                                            href={`${SITE_URLS.mySchedule.replace(':uuid', `${item.uuid}`)}`}
                                                            mode={AnchorMode.none}>
                                                            <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                                <i className="fa-light fa-calendar-days"></i>
                                                            </Typography>
                                                        </Anchor>
                                                    ),
                                                item.status === 'active' && (
                                                    <Anchor
                                                        key="option-2"
                                                        href={SITE_URLS.merchantDetail.replace(':uuid', `${item.uuid}`)}
                                                        mode={AnchorMode.none}>
                                                        <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                            <i className="fa-light fa-eye" />
                                                        </Typography>
                                                    </Anchor>
                                                ),
                                                item.can_have_products && item.status !== 'pending' && (
                                                    <Anchor
                                                        key="option-3"
                                                        href={`${SITE_URLS.myProducts}?store=${item.uuid}`}
                                                        mode={AnchorMode.none}>
                                                        <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                            <i className="fa-light fa-tag" />
                                                        </Typography>
                                                    </Anchor>
                                                ),
                                                <Anchor
                                                    key="option-4"
                                                    href={SITE_URLS.myCompaniesEdit.replace(':uuid', `${item.uuid}`)}
                                                    mode={AnchorMode.none}>
                                                    <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                        <i className="fa-light fa-pen-to-square" />
                                                    </Typography>
                                                </Anchor>,
                                                item.status !== 'pending' && (
                                                    <button
                                                        key="option-5"
                                                        disabled={loadingUpdateState}
                                                        onClick={() => handleOnUpdateState(item.uuid || '')}>
                                                        <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                            {item.status === 'active' ? (
                                                                <i
                                                                    className="fa-light fa-toggle-on"
                                                                    title={translations.deactivate}
                                                                />
                                                            ) : (
                                                                <i
                                                                    className="fa-light fa-toggle-off"
                                                                    title={translations.activate}
                                                                />
                                                            )}
                                                        </Typography>
                                                    </button>
                                                ),
                                            ].filter(Boolean) as JSX.Element[]
                                        }
                                    />
                                ),
                            },
                        ];
                    })}
                />
            </Card>
        </div>
    );
};
